/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Flatpickr from "react-flatpickr";
import BreadCrumb from 'Common/BreadCrumb';
import { Link } from 'react-router-dom';
import { Dropdown } from 'Common/Components/Dropdown';
import TableContainer from 'Common/TableContainer';

// Icons
import { Search, Plus, MoreHorizontal, FileEdit, PlusCircle, Trash2, Settings } from 'lucide-react';
import Modal from 'Common/Components/Modal';

// react-redux
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  onGetCustomerList,
  onAddCustomer,
  onUpdateCustomer,
} from 'slices/thunk';
import { toast, ToastContainer } from 'react-toastify';
import filterDataBySearch from 'Common/filterDataBySearch';
import moment from 'moment';
import { doGetProjectSetting, doPatchProjectSetting } from '../../services/project.service';

const CustomerPage = () => {
  const dispatch = useDispatch<any>();

  const selectDataList = createSelector(
    (state: any) => state.Customers,
    (customer) => ({
      customerList: customer.customerList
    })
  );

  const { customerList } = useSelector(selectDataList);
  const [customer, setCustomer] = useState<any>([]);
  const [eventData, setEventData] = useState<any>();

  const [show, setShow] = useState<boolean>(false);
  const [showCustomerType, setShowCustomerType] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false)

  const [customerTypes, setCustomerTypes] = useState<Array<string>>([])

  // Get Data
  useEffect(() => {
    dispatch(onGetCustomerList())
  }, [dispatch])

  useEffect(() => {
    setCustomer(customerList)
  }, [customerList])

  // Update Data
  const handleUpdateDataClick = (ele: any) => {
    setEventData({ ...ele })
    setIsEdit(true)
    setShow(true)
  }

  // validation
  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      userId: (eventData && eventData.userId) || '',
      name: (eventData && eventData.name) || '',
      country: (eventData && eventData.country) || '',
      type: (eventData && eventData.type) || '',
      email: (eventData && eventData.email) || '',
      phoneNo: (eventData && eventData.phoneNo) || '',
      address: (eventData && eventData.address) || '',
    },
    validationSchema: Yup.object({
      img: Yup.string(),
      name: Yup.string().required("Please Enter Name"),
      country: Yup.string().required("Please Enter Country"),
      type: Yup.string().optional(),
      email: Yup.string().optional(),
      phoneNo: Yup.string().required("Please Enter Phone Number"),
      address: Yup.string(),
    }),

    onSubmit: (values) => {
      if (isEdit) {
        const updateCustomer = {
          ...values,
        };
        // update user
        dispatch(onUpdateCustomer(updateCustomer))
      } else {
        delete values.userId;
        const newCustomer = {
          ...values,
        };
        // save new user
        dispatch(onAddCustomer(newCustomer));
      }
      toggle()
    },
  })

  const customerTypeValidation: any = useFormik({
    initialValues: {
      customerTypes: [],
    },
    validationSchema: Yup.object({
      customerTypes: Yup.array().of(Yup.string()).required("Please Enter Customer Type"),
    }),
    onSubmit: (values) => {
      const customerTypes = values.customerTypes.filter((item: string) => item).map((item: string) => item.toLowerCase())
      doPatchProjectSetting({ customerTypes }).then((res: any) => {
        toast.success('Customer type updated successfully', { autoClose: 2000 })
        setShowCustomerType(false)
        setCustomerTypes(res.customerTypes)
      }).catch((error) => {
        console.log(error)
      })
    },
  })

  //
  const toggle = useCallback(() => {
    if (show) {
      setShow(false)
      setEventData("")
      setIsEdit(false)
    } else {
      setShow(true)
      setEventData("")
      validation.resetForm()
    }
  }, [show, validation])

  const toggleCustomerType = useCallback(() => {
    if (showCustomerType) {
      setShowCustomerType(false)
    } else {
      setShowCustomerType(true)
    }
  }, [showCustomerType])

  // Search Data
  const filterSearchData = (e: any) => {
    const search = e.target.value
    const keysToSearch = ['name', 'country', 'email']
    filterDataBySearch(customerList, search, keysToSearch, setCustomer)
  }

  useEffect(() => {
    doGetProjectSetting().then((res: any) => {
      customerTypeValidation.setValues({
        customerTypes: res.customerTypes || [],
      })
      setCustomerTypes(res.customerTypes)
    })
  }, [])

  const columns = useMemo(() => [
    {
      header: (
        <div className="flex items-center h-full">
          <input id="CheckboxAll" className="w-4 h-4 bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800 cursor-pointer" type="checkbox" />
        </div>
      ),
      enableSorting: false,
      id: "checkAll",
      cell: (cell: any) => {
        return (
          <div className="flex items-center h-full">
            <input id="Checkbox1" className="w-4 h-4 bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800 cursor-pointer" type="checkbox" />
          </div>
        );
      }
    },
    {
      header: "Name",
      accessorKey: "name",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div className="flex items-center gap-2">
          <div className="grow">
            <h6 className="mb-1">{cell.getValue()}</h6>
            <p className="text-slate-500 dark:text-zink-200">{cell.row.original.type}</p>
          </div>
        </div>
      ),
    },
    {
      header: "Phone Number",
      accessorKey: "phoneNo",
      enableColumnFilter: false,
    },
    {
      header: "Email",
      accessorKey: "email",
      enableColumnFilter: false,
    },
    {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => (
        <Dropdown className="relative">
          <Dropdown.Trigger className="flex items-center justify-center w-[30px] h-[30px] p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20" id={cell.row.original.userId}>
            <MoreHorizontal className="w-3 h-3" />
          </Dropdown.Trigger>
          <Dropdown.Content placement="right-end" className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right list-none bg-white rounded-md shadow-md min-w-[10rem] dark:bg-zink-600" aria-labelledby={cell.row.original.userId}>
            <li>
              <Link data-modal-target="addCustomerModal" className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200" to="#!"
                onClick={() => {
                  const data = cell.row.original;
                  handleUpdateDataClick(data);
                }}>
                <FileEdit className="inline-block w-3 h-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Edit</span></Link>
            </li>
          </Dropdown.Content>
        </Dropdown>
      ),
    }
  ], []
  );

  return (
    <React.Fragment>
      <BreadCrumb title='List View' pageTitle='Customers' />
      <ToastContainer closeButton={false} limit={1} />
      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
        <div className="xl:col-span-12">
          <div className="card" id="customersTable">
            <div className="card-body">
              <div className="flex items-center">
                <h6 className="text-15 grow">Customers List</h6>
                <div className="shrink-0">
                  <button type="button" className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20" onClick={toggleCustomerType}><Settings className="inline-block w-4 h-4" /> <span className="align-middle">Configure Customer Type</span></button>
                  <button type="button" className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 ml-2" onClick={toggle}><Plus className="inline-block w-4 h-4" /> <span className="align-middle">Add Customer</span></button>
                </div>
              </div>
            </div>
            <div className="!py-3.5 card-body border-y border-dashed border-slate-200 dark:border-zink-500">
              <form action="#!">
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                  <div className="relative xl:col-span-2">
                    <input type="text" className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Search for name, email, phone number etc..." autoComplete="off" onChange={(e) => filterSearchData(e)} />
                    <Search className="inline-block w-4 h-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>
              </form>
            </div>
            <div className="card-body">
              {customer && customer.length > 0 ?
                <TableContainer
                  isPagination={true}
                  columns={(columns || [])}
                  data={(customer || [])}
                  customPageSize={10}
                  divclassName="-mx-5 -mb-5 overflow-x-auto"
                  tableclassName="w-full border-separate table-custom border-spacing-y-1 whitespace-nowrap"
                  theadclassName="text-left relative rounded-md bg-slate-100 dark:bg-zink-600 after:absolute ltr:after:border-l-2 rtl:after:border-r-2 ltr:after:left-0 rtl:after:right-0 after:top-0 after:bottom-0 after:border-transparent [&.active]:after:border-custom-500 [&.active]:bg-slate-100 dark:[&.active]:bg-zink-600"
                  thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold"
                  tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5"
                  PaginationClassName="flex flex-col items-center mt-8 md:flex-row"
                />
                :
                (<div className="noresult">
                  <div className="py-6 text-center">
                    <Search className="w-6 h-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                    <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                    <p className="mb-0 text-slate-500 dark:text-zink-200">We've searched more than 199+ customers We did not find any customers for you search.</p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>


      {/* Customer Modal  */}
      <Modal show={show} onHide={toggle} id="defaultModal" modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600">
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500">
          <Modal.Title className="text-16">{!!isEdit ? "Edit Customer" : "Add Customer"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form action="#!" onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}>

            <div className="mb-3">
              <label htmlFor="userNameInput" className="inline-block mb-2 text-base font-medium">Name</label>
              <input type="text" id="userNameInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Enter name"
                name="name"
                onChange={validation.handleChange}
                value={validation.values.name || ""}
              />
              {validation.touched.name && validation.errors.name ? (
                <p className="text-red-400">{validation.errors.name}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="dobInput" className="inline-block mb-2 text-base font-medium">Date Of Birth</label>
              <Flatpickr
                id="dobInput"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                options={{
                  dateFormat: "d M, Y"
                }}
                placeholder='Select date' name='dob'
                onChange={(date: any) => validation.setFieldValue("dob", moment(date[0]).format("DD MMMM ,YYYY"))}
                value={validation.values.dob || ''}
              />
              {validation.touched.dob && validation.errors.dob ? (
                <p className="text-red-400">{validation.errors.dob}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="emailInput" className="inline-block mb-2 text-base font-medium">Email</label>
              <input type="email" id="emailInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Enter email"
                name="email"
                onChange={validation.handleChange}
                value={validation.values.email || ""}
              />
              {validation.touched.email && validation.errors.email ? (
                <p className="text-red-400">{validation.errors.email}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="phoneNoInput" className="inline-block mb-2 text-base font-medium">Phone Number</label>
              <input type="text" id="phoneNoInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="+62811111111"
                name="phoneNo"
                onChange={validation.handleChange}
                value={validation.values.phoneNo || ""}
              />
              {validation.touched.phoneNo && validation.errors.phoneNo ? (
                <p className="text-red-400">{validation.errors.phoneNo}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="countryInput" className="inline-block mb-2 text-base font-medium">Country</label>
              <input type="text" id="countryInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Country"
                name="country"
                onChange={validation.handleChange}
                value={validation.values.country || ""}
              />
              {validation.touched.country && validation.errors.country ? (
                <p className="text-red-400">{validation.errors.country}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="addressInput" className="inline-block mb-2 text-base font-medium">Address</label>
              <input type="text" id="addressInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Address"
                name="address"
                onChange={validation.handleChange}
                value={validation.values.address || ""}
              />
              {validation.touched.address && validation.errors.address ? (
                <p className="text-red-400">{validation.errors.address}</p>
              ) : null}
            </div>
            {
              customerTypes?.length > 0 && (
                <div className="mb-3">
                  <label htmlFor="customerTypeInput" className="inline-block mb-2 text-base font-medium">Type</label>
                  <Trash2 className="inline-block w-4 h-4 ml-2 text-red-500"
                    onClick={() => {
                      validation.setFieldValue('type', '')
                    }} />
                  <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                    {
                      customerTypes?.map((item: any, index: number) => (
                        <div className="flex items-center gap-3" key={index}>
                          <input id={`customerTypeOption${index}`}
                            className="w-4 h-4 border rounded-full appearance-none cursor-pointer bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-purple-500 checked:border-purple-500 dark:checked:bg-purple-500 dark:checked:border-purple-500 checked:disabled:bg-purple-400 checked:disabled:border-purple-400 peer"
                            type="radio"
                            name="type"
                            value={item}
                            checked={validation.values.type === item}
                            onChange={validation.handleChange} />
                          <label
                            htmlFor={`customerTypeOption${index}`}
                            className="flex flex-col gap-4 p-5 border rounded-md cursor-pointer md:flex-row border-slate-200 dark:border-zink-500 peer-checked:border-purple-500 dark:peer-checked:border-purple-700 grow">
                            <span className="grow">
                              <span className="block mb-1 font-semibold text-15">{item}</span>
                            </span>
                          </label>
                        </div>
                      ))
                    }
                  </div>
                  {validation.touched.type && validation.errors.type ? (
                    <p className="text-red-400">{validation.errors.type}</p>
                  ) : null}
                </div>
              )
            }
            <div className="flex justify-end gap-2 mt-4">
              <button type="reset" data-modal-close="addDocuments" className="text-red-500 transition-all duration-200 ease-linear bg-white border-white btn hover:text-red-600 focus:text-red-600 active:text-red-600 dark:bg-zink-500 dark:border-zink-500" onClick={toggle}>Cancel</button>
              <button type="submit" className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20">
                {!!isEdit ? "Update Customer" : "Add Customer"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={showCustomerType} onHide={toggleCustomerType} id="customerTypeModal" modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600">
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500">
          <Modal.Title className="text-16">{"Customer Type"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form action="#!" onSubmit={(e) => {
            e.preventDefault();
            customerTypeValidation.handleSubmit();
            return false;
          }}>

            <div className="mb-3">
              <label htmlFor="userNameInput" className="inline-block mb-2 text-base font-medium">Type</label>
              <PlusCircle className="inline-block w-4 h-4 ml-2 text-green-500" onClick={() => {
                const newCustomerType = [...customerTypeValidation.values.customerTypes]
                newCustomerType.push('')
                customerTypeValidation.setFieldValue('customerTypes', newCustomerType)
              }} />
              {
                customerTypeValidation.values.customerTypes.map((item: any, index: number) => (
                  <div className="mb-3 flex items-center" key={index}>
                    <input
                      type="text"
                      id={`customerTypeInput${index}`}
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 w-3/4"
                      placeholder="Enter type"
                      name={`customerTypes[${index}]`}
                      onChange={customerTypeValidation.handleChange}
                      value={customerTypeValidation.values.customerTypes[index] || ''}
                    />
                    <Trash2 className="inline-block w-4 h-4 ml-2 text-red-500"
                      onClick={() => {
                        const newCustomerType = [...customerTypeValidation.values.customerTypes]
                        newCustomerType.splice(index, 1)
                        customerTypeValidation.setFieldValue('customerTypes', newCustomerType)
                      }} />
                  </div>
                ))
              }
              {customerTypeValidation.touched.customerType && customerTypeValidation.errors.customerType ? (
                <p className="text-red-400">{customerTypeValidation.errors.customerType}</p>
              ) : null}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="reset" data-modal-close="customerTypeModal" className="text-red-500 transition-all duration-200 ease-linear bg-white border-white btn hover:text-red-600 focus:text-red-600 active:text-red-600 dark:bg-zink-500 dark:border-zink-500" onClick={toggleCustomerType}>Cancel</button>
              <button type="submit" className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20">
                {"Save"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </React.Fragment >
  );
};

export default CustomerPage;
